import { useEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { ProductImg, isPlaceholder } from "./ProductImg";

// Premium editorial product viewer:
// - Light minimal stage (off-white) so the cap is the hero
// - Larger, square, generously padded
// - Smooth fade+scale crossfade when the active image changes
// - Hover-zoom on desktop, tap opens fullscreen lightbox
export const ProductImageZoom = ({ src, alt, onOpenLightbox }) => {
  const wrapRef = useRef(null);
  const [zooming, setZooming] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [visibleSrc, setVisibleSrc] = useState(src);
  const [entering, setEntering] = useState(false);
  const placeholder = isPlaceholder(src);

  // Fade transition between gallery images
  useEffect(() => {
    if (src === visibleSrc) return;
    setEntering(true);
    const t1 = setTimeout(() => {
      setVisibleSrc(src);
      setEntering(false);
    }, 220);
    return () => clearTimeout(t1);
  }, [src, visibleSrc]);

  const handleMove = (e) => {
    if (placeholder) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={() => !placeholder && setZooming(true)}
      onMouseLeave={() => setZooming(false)}
      onMouseMove={handleMove}
      onClick={onOpenLightbox}
      data-testid="product-zoom-image"
      className={`relative aspect-square w-full overflow-hidden group rounded-sm ${
        placeholder ? "cursor-pointer" : "cursor-zoom-in"
      }`}
      style={{
        background:
          "radial-gradient(120% 120% at 50% 20%, #FAFAF7 0%, #EFEEEA 60%, #E5E4DF 100%)",
      }}
    >
      {/* Subtle frame */}
      <div className="pointer-events-none absolute inset-0 ring-1 ring-black/[0.06]" />

      {/* Editorial padding stage */}
      <div className="absolute inset-6 md:inset-10 lg:inset-14 flex items-center justify-center">
        {placeholder ? (
          <ProductImg src={visibleSrc} className="w-full h-full" />
        ) : (
          <img
            src={visibleSrc}
            alt={alt}
            draggable={false}
            className={`max-w-full max-h-full object-contain select-none transition-all duration-500 ease-out ${
              entering ? "opacity-0 scale-[0.985]" : "opacity-100 scale-100"
            } ${zooming ? "scale-[1.55]" : ""}`}
            style={
              zooming ? { transformOrigin: `${pos.x}% ${pos.y}%` } : undefined
            }
          />
        )}
      </div>

      {/* Fullscreen affordance */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenLightbox();
        }}
        data-testid="open-lightbox-button"
        className="absolute top-4 right-4 p-2.5 bg-white/85 backdrop-blur-sm border border-black/10 opacity-0 group-hover:opacity-100 transition-opacity hover:border-black hover:text-black text-black/70"
        aria-label="Fullscreen"
      >
        <Maximize2 className="w-4 h-4" />
      </button>
    </div>
  );
};
